import { type JSX, Show } from 'solid-js';

// Full-page error layout shared by the load-error, crash, 403 and authorize-error screens.
// Each art is an animated SVG; keyframes live in index.css under `errorart-*`.
export interface ErrorScreenProps {
  art: JSX.Element;
  title: string;
  body: JSX.Element;
  action?: JSX.Element;
}

export const ErrorScreen = (props: ErrorScreenProps): JSX.Element => (
  <main class="grid min-h-screen place-items-center overflow-hidden bg-neutral-50 p-4 dark:bg-[#0b1020]">
    <div class="flex w-full max-w-md flex-col items-center text-center">
      {props.art}
      <div class="errorart-copy flex flex-col items-center">
        <h1 class="mt-6 mb-2 text-2xl font-semibold text-neutral-900 sm:text-3xl dark:text-neutral-100">
          {props.title}
        </h1>
        <p class="mb-6 text-sm text-neutral-500 dark:text-neutral-400">{props.body}</p>
        <Show when={props.action}>{props.action}</Show>
      </div>
    </div>
  </main>
);

// Flash + spark burst centered on (x, y); `delay` is when it fires.
const Burst = (props: { x: number; y: number; delay: string }): JSX.Element => (
  <g transform={`translate(${props.x} ${props.y})`} style={`--errorart-delay: ${props.delay}`}>
    <circle class="errorart-flash" r="18" />
    <circle class="errorart-spark" r="3" style="--dx: -40px; --dy: -30px" />
    <circle class="errorart-spark" r="2.5" style="--dx: 36px; --dy: -38px" />
    <circle class="errorart-spark" r="2" style="--dx: -30px; --dy: 34px" />
    <circle class="errorart-spark" r="3" style="--dx: 42px; --dy: 26px" />
    <circle class="errorart-spark" r="2" style="--dx: 4px; --dy: -48px" />
    <circle class="errorart-spark" r="2.5" style="--dx: -6px; --dy: 46px" />
  </g>
);

const HOLE = 'fill-neutral-50 dark:fill-[#0b1020]';
const ART = 'overflow-visible text-slate-700 dark:text-slate-300';

/** Plug and socket snap apart; while `retrying` they rejoin, and dropping it replays the snap. */
export const UnpluggedArt = (props: { retrying?: boolean }): JSX.Element => (
  <svg
    class={`errorart-bob w-[min(340px,86vw)] ${ART}`}
    classList={{ 'errorart-retrying': props.retrying }}
    viewBox="0 0 340 120"
    aria-hidden="true"
  >
    <g class="errorart-plug-left">
      <g class="errorart-dangle-left">
        <path class="errorart-cable" d="M0 95 C 50 95, 70 60, 120 60" />
        <rect x="120" y="44" width="30" height="32" rx="6" fill="currentColor" />
        <rect x="150" y="50" width="12" height="5" rx="2" fill="currentColor" />
        <rect x="150" y="65" width="12" height="5" rx="2" fill="currentColor" />
      </g>
    </g>
    <g class="errorart-plug-right">
      <g class="errorart-dangle-right">
        <path class="errorart-cable" d="M340 25 C 290 25, 270 60, 220 60" />
        <rect x="178" y="44" width="42" height="32" rx="6" fill="currentColor" />
        <rect x="182" y="50" width="8" height="5" rx="1" class={HOLE} />
        <rect x="182" y="65" width="8" height="5" rx="1" class={HOLE} />
      </g>
    </g>
    <Burst x={170} y={60} delay="380ms" />
  </svg>
);

/** Padlock drops in, its shackle slams shut with a spark, then the lock shakes. */
export const LockArt = (): JSX.Element => (
  <svg class={`w-[min(200px,60vw)] ${ART}`} viewBox="0 0 200 160" aria-hidden="true">
    <circle class="errorart-ring" cx="100" cy="100" r="56" />
    <g class="errorart-lock">
      <path
        class="errorart-shackle"
        d="M74 74 V50 a26 26 0 0 1 52 0 V74"
        fill="none"
        stroke="currentColor"
        stroke-width="12"
        stroke-linecap="round"
      />
      <rect x="56" y="70" width="88" height="70" rx="14" fill="currentColor" />
      <g class="errorart-keyhole">
        <circle cx="100" cy="98" r="9" />
        <rect x="96" y="100" width="8" height="20" rx="3" />
      </g>
    </g>
    <Burst x={100} y={70} delay="700ms" />
  </svg>
);

/** ID card slides in and gets slammed with a red stamp. */
export const DeniedCardArt = (props: { stamp: string }): JSX.Element => (
  <svg class={`w-[min(280px,80vw)] ${ART}`} viewBox="0 0 240 160" aria-hidden="true">
    <g class="errorart-card">
      <rect
        x="40"
        y="30"
        width="160"
        height="100"
        rx="12"
        fill="none"
        stroke="currentColor"
        stroke-width="4"
      />
      <circle cx="80" cy="68" r="16" fill="currentColor" />
      <rect x="110" y="56" width="70" height="8" rx="4" fill="currentColor" />
      <rect x="110" y="74" width="48" height="8" rx="4" fill="currentColor" opacity="0.6" />
      <rect x="60" y="104" width="120" height="6" rx="3" fill="currentColor" opacity="0.35" />
    </g>
    <g class="errorart-stamp">
      <rect x="58" y="58" width="124" height="44" rx="6" />
      <text x="120" y="88" text-anchor="middle">
        {props.stamp}
      </text>
    </g>
    <Burst x={120} y={80} delay="900ms" />
  </svg>
);
